import { HttpEvent, HttpEventType, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';

@Injectable()
export class QueuedRequestInterceptorService implements HttpInterceptor {
  private readonly MAX_QUEUE_SIZE: number = 10;
  private queue: ReplaySubject<any>[] = [];

  /**
   * Intercepts HTTP requests and manages a queue of admissible requests.
   *
   * This method ensures that only admissible requests are processed and queued.
   * Requests in the queue are handled sequentially, and the next request is dispatched
   * only after the current one completes or encounters an error.
   *
   * @param request - The HTTP request to be intercepted.
   * @param next - The HTTP handler to process the request.
   * @returns An observable of the HTTP event stream.
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRequestAdmissible(request)) {
      return next.handle(request);
    }

    const requestQueueItem$ = new ReplaySubject<any>();
    const result$ = requestQueueItem$.pipe(
      take(1),
      switchMap(() =>
        next.handle(request).pipe(
          tap((req) => {
            if (req.type == HttpEventType.Response) {
              this.processNextRequest();
            }
          }),
          catchError((err) => {
            this.processNextRequest();
            throw err;
          })
        )
      )
    );

    this.queue.push(requestQueueItem$);

    if (this.queue.length > this.MAX_QUEUE_SIZE) {
      console.warn('Queue size exceeded the maximum limit');
    }

    if (this.queue.length === 1) {
      this.dispatchRequest();
    }

    return result$;
  }

  /**
   * Processes the next request in the queue.
   *
   * This method checks if there are any requests in the queue. If the queue is empty,
   * it exits early. Otherwise, it retrieves and completes the next request in the queue
   * and then dispatches the next request in line.
   *
   * @private
   */
  private processNextRequest(): void {
    if (this.queue.length === 0) {
      return;
    }

    const completedRequest$ = this.queue.shift();
    if (completedRequest$) {
      completedRequest$.complete();
    }

    this.dispatchRequest();
  }

  /**
   * Dispatches the next request in the queue if the queue is not empty.
   *
   * This method checks if there are any requests in the queue. If the queue
   * contains at least one request, it retrieves the first request (observable)
   * from the queue and triggers its execution by calling `next()` on it.
   *
   * @private
   */
  private dispatchRequest(): void {
    if (this.queue.length === 0) {
      return;
    }

    const nextSub$ = this.queue[0];
    nextSub$.next();
  }

  /**
   * Determines whether a given HTTP request is admissible based on specific criteria.
   *
   * A request is considered admissible if:
   * - The HTTP method is `PATCH`.
   * - The request URL contains the substring 'slide'.
   *
   * @param request - The HTTP request to evaluate.
   * @returns `true` if the request meets the admissibility criteria, otherwise `false`.
   */
  private isRequestAdmissible(request: HttpRequest<any>): boolean {
    return request.method === 'PATCH' && request.url.includes('slide');
  }
}
